import { TamediaStyles } from "@ta-interaktiv/microfrontend-template-helpers";
import { FC, ReactElement, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { TextWidthExtended } from '@ta-interaktiv/react-disco-grid'
import { SwissMap } from 'components/ChoroplethMap'
import { GlobalVariables } from 'Globals/GlobalVariables'
import pricesCSV from 'Assets/output_Karte.csv'
import * as d3 from 'd3'
import { COLOR_PALETTE } from 'Constants/ColorPalette'
import ColorLegend from 'components/ColorLegend'
import ToolTip from 'components/ToolTip'

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties,
}): ReactElement => {

  const [minValue, setMinValue] = useState<number>(15)
  const [maxValue, setMaxValue] = useState<number>(35)
  const [pricesPerMunicipality, setPricesPerMunicipality] =
    useState<PricePerMunicipality[]>()
  const [activeMunicipality, setActiveMunicipality] =
    useState<PricePerMunicipality>()
  const [foundMunicipalityFromUrl, setFoundMunicipalityFromUrl] =
    useState(false)
  const [foundMunicipalityIdFromUrl, setFoundMunicipalityIdFromUrl] =
    useState<number>()
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false)

  useEffect(() => {
    d3.csv(pricesCSV).then((prices) => {
      const parsedPrices = prices.map((obj) => {
        const newString = obj?.json_string ?? '{}'
        const parsedString = JSON.parse(newString == '' ? '[]' : newString)
        return {
          ...obj,
          json_string: parsedString,
        }
      }) as PricePerMunicipality[]

      setPricesPerMunicipality(parsedPrices)
    })
  }, [])
  useEffect(() => {
    console.log('[foundMunicipalityIdFromUrl,pricesPerMunicipality]', [
      foundMunicipalityIdFromUrl,
      pricesPerMunicipality,
    ])
    if (foundMunicipalityFromUrl && foundMunicipalityIdFromUrl) {
      const foundMuni = pricesPerMunicipality?.find(
        (data) => +data.bfsId === +foundMunicipalityIdFromUrl
      )
      console.log('foundMuni', foundMuni)
      setActiveMunicipality(foundMuni)
    }
  }, [foundMunicipalityIdFromUrl, pricesPerMunicipality])
  useEffect(() => {
    // Get params from URL and set active municipality
    const searchParams = new URL(String(window.location)).searchParams
    const muniParam = searchParams.get(
      tenant.lang == 'fr' ? 'commune' : 'gemeinde'
    )
    const bfsIdFromUrl = muniParam && parseInt(muniParam)

    if (bfsIdFromUrl) {
      setFoundMunicipalityFromUrl(true)
      console.log('🔍 Found municipality from URL', bfsIdFromUrl)
      const foundMuni = pricesPerMunicipality?.find(
        (data) => +data.bfsId === +bfsIdFromUrl
      )
      setFoundMunicipalityIdFromUrl(bfsIdFromUrl)
      setActiveMunicipality(foundMuni)
    }

    const handleEvent = (event: CustomEvent) => {
      console.log('📫 GDE-Change received')
      const newBfsId = event.detail
      if (newBfsId) {
        const foundMuni = pricesPerMunicipality?.find(
          (data) => +data.bfsId === +newBfsId
        )
        setActiveMunicipality(foundMuni)
      }
    }
    window.addEventListener('changeGDE', handleEvent as EventListener)

    const buttonUp = document?.querySelector('[class*=BackToTop_root]')
    buttonUp?.setAttribute('style', 'opacity: 0')
    return () => {
      window.removeEventListener('changeGDE', handleEvent as EventListener)
    }
  }, [])
  useEffect(() => {
    activeMunicipality &&
      foundMunicipalityFromUrl &&
      setFoundMunicipalityFromUrl(false)
  }, [activeMunicipality])
  const suffix = tenant.lang === 'fr' ? ' ct/kWh' : ' Rp./KWh'
  return (
    <AppContainer $colorMode={colorMode}>
      <MapSection>
        {pricesPerMunicipality && (
          <>
            <SwissMap
              // topoJson={chTopoJson}
              municipalitySearchYear={'20230101_' + tenant.lang}
              colorPalette={COLOR_PALETTE}
              ToolTip={ToolTip}
              locale={tenant?.lang ?? 'de'}
              // municipalityStrokeColor='#80808078'
              cantonStrokeWidth='2'
              propertyNameToCompare={'tarif_min_new'}
              municipalityData={pricesPerMunicipality}
              activeMunicipalityId={activeMunicipality?.bfsId}
              newActiveMunicipalityIdHandler={(newBfsId) =>
                setActiveMunicipality(
                  pricesPerMunicipality.find(
                    (data) => +data.bfsId === +newBfsId
                  )
                )
              }
              hideMunicipalitySearch={false}
              minValue={minValue}
              maxValue={maxValue}
              topoJsonYear={2023}
              fullscreenChangeHandler={(isFullscreenNewVal) => {
                setIsFullscreen(isFullscreenNewVal)
              }}
              isFullscreen={isFullscreen}
              scrollIntoView={!foundMunicipalityFromUrl}
            />
            <ColorLegendWrapper>
              <ColorLegend
                colorPalette={COLOR_PALETTE}
                customLabels={[
                  '<' + minValue + suffix,
                  (maxValue + minValue) / 2 + suffix,
                  '>' + maxValue + suffix,
                ]}
                numberOfTicks={3}
                minVal={minValue}
                maxVal={maxValue}
                currentValue={
                  activeMunicipality?.tarif_min_new &&
                  +activeMunicipality?.tarif_min_new > 0
                    ? +activeMunicipality?.tarif_min_new
                    : undefined
                }
              />
            </ColorLegendWrapper>
          </>
        )}
        {activeMunicipality && (
          <>
            <ToolTipWrapper isFullscreen={isFullscreen}>
              <ToolTip
                activeData={activeMunicipality}
                locale={tenant.lang}
                closePressed={() => setActiveMunicipality(undefined)}
                isClosable={true}
                hasShadow={!isFullscreen}
              />
            </ToolTipWrapper>
          </>
        )}
      </MapSection>
    </AppContainer>
  )
}
const AppContainer = styled(TamediaStyles)`${GlobalVariables}
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  touch-action: pan-x pan-y;
`
const ToolTipWrapper = styled.div<{ isFullscreen: boolean }>`
  pointer-events: none;
  z-index: 102;
  ${({ isFullscreen }) =>
    isFullscreen &&
    css`
      /* @media (max-width: 768px) { */
      left: 0.5em;
      right: 0.5em;
      position: sticky;
      bottom: 3.5em;
      /* } */
    `}
`
const ColorLegendWrapper = styled.div`
  padding: 0 1em;
  overflow: hidden;
`
const MapSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  margin-bottom: 3rem;
  @media (min-width: 768px) {
    min-height: 523px;
  }
  #swiss-choropleth-map {
    path {
      transition: fill 0.5s cubic-bezier(0.645, 0.045, 0.355, 1);
    }
    svg {
      #country-border {
        /* fill: url(#diagonalHatch); */
        vector-effect: non-scaling-stroke;
      }
      .active-municipality {
        stroke: white;
        stroke-width: 2px;
      }
      .municipality {
        // only hover on desktop
        @media (hover: hover) {
          &:hover {
            stroke: white;
            stroke-width: 1.5px;
          }
        }
      }
    }
  }

  .municipality-search {
    margin-bottom: 2em;
    padding: 0 1em;
    svg {
      stroke: var(--text-color) !important;
    }
    #search {
      font-size: 18px;
      background-color: var(--selection-background);
      color: var(--text-color);
    }
    .results {
      overflow: hidden;
      z-index: 102;
      @media (min-width: 768px) {
        position: absolute;
      }
      display: flex;
      .result {
        max-width: 100%;
        background-color: var(--selection-background);
        color: currentColor;
      }
      flex-direction: column;
      pointer-events: auto;
      background-color: none;
    }
  }
`
export default MainComponent
